import { InvalidTriggerConfigError } from '../../errors'
import { getPath } from '../path'
import type { HandlerBag } from '../types'
import type {
  DedupeConfig,
  PollingInvocation,
  PollingPollBag,
  PollingResult,
  PollingTriggerProps,
  WatermarkConfig,
} from './types'

// How a polling trigger decides what is NEW (08-polling-triggers.md). Three strategies, of which an author
// declares exactly one:
//
//   dedupe    — remember the ids already seen, emit the rest        (unordered feeds, no reliable timestamp)
//   watermark — remember the newest marker, emit anything past it   (ordered feeds with a cursor field)
//   poll      — the author owns the whole { events, state } contract (escape hatch)
//
// The first two share a lifecycle the base class owns, so a strategy only implements its own delta:
//   • first poll (no state) → SEED the state, emit nothing   (wiring up must not fire a run)
//   • learningMode          → emit the fetched head, state null (a sample for setup; nothing persisted)
//   • thereafter            → emit the delta, advance the state
//
// The runtime drives the interval and the persistence; this file only computes `{ events, state }`.

// What one invocation gives a strategy to work with.
interface PollCycle {
  bag: HandlerBag
  params: Record<string, unknown>
  isLearningMode: boolean
  state: Record<string, unknown> | null
}

type FetchFn = (bag: HandlerBag & { params: Record<string, unknown>; isLearningMode: boolean }) =>
  unknown[] | Promise<unknown[]>

export abstract class PollingMechanism {
  /**
   * Pick the strategy an author declared, rejecting the combinations that cannot work. Runs at REGISTRATION
   * time, so a misconfigured trigger fails while the service module is being required rather than on some
   * later tick in production.
   */
  static from(props: PollingTriggerProps, serviceId: string): PollingMechanism {
    const declared = (['dedupe', 'watermark', 'poll'] as const)
      .filter(key => props[key] != null)

    if (declared.length !== 1) {
      throw new InvalidTriggerConfigError(
        serviceId,
        `polling trigger "${ props.id }" must declare exactly one of dedupe|watermark|poll (got: ${ declared.join(', ') || 'none' })`,
        { triggerId: props.id }
      )
    }

    if (props.poll) {
      return new CustomPollMechanism(props.poll)
    }

    // dedupe and watermark both filter a fetched list — without `fetch` there is nothing to filter.
    if (typeof props.fetch !== 'function') {
      throw new InvalidTriggerConfigError(
        serviceId,
        `polling trigger "${ props.id }" with dedupe/watermark requires a fetch(...) function`,
        { triggerId: props.id }
      )
    }

    return props.dedupe
      ? new DedupeMechanism(props.fetch as FetchFn, props.dedupe)
      : new WatermarkMechanism(props.fetch as FetchFn, props.watermark!)
  }

  run(invocation: PollingInvocation, bag: HandlerBag): Promise<PollingResult> {
    return this.poll({
      bag,
      params: invocation.triggerData ?? {},
      isLearningMode: !!invocation.learningMode,
      state: invocation.state ?? null,
    })
  }

  protected abstract poll(cycle: PollCycle): Promise<PollingResult>
}

// The escape hatch: the author owns fetching, comparison and state.
class CustomPollMechanism extends PollingMechanism {
  constructor(private readonly fn: NonNullable<PollingTriggerProps['poll']>) {
    super()
  }

  protected async poll({ bag, params, state, isLearningMode }: PollCycle): Promise<PollingResult> {
    return this.fn({ ...bag, params, state, isLearningMode } as PollingPollBag)
  }
}

// The shared lifecycle of the two strategies that filter a fetched list.
abstract class FetchingMechanism extends PollingMechanism {
  constructor(private readonly fetch: FetchFn) {
    super()
  }

  protected async poll({ bag, params, state, isLearningMode }: PollCycle): Promise<PollingResult> {
    const items = await this.fetch({ ...bag, params, isLearningMode })

    // Learning mode: emit the fetched head as the sample; do not persist state.
    if (isLearningMode) {
      return { events: items, state: null }
    }

    return this.delta(items, state, params)
  }

  // What is new since `state`, and the state to remember next time.
  protected abstract delta(
    items: unknown[],
    state: Record<string, unknown> | null,
    params: Record<string, unknown>
  ): PollingResult
}

// ── dedupe (seen-set) ────────────────────────────────────────────────────────

class DedupeMechanism extends FetchingMechanism {
  readonly #by: (item: unknown) => unknown
  readonly #stateKey: string
  readonly #max: number

  constructor(fetch: FetchFn, config: DedupeConfig) {
    super(fetch)

    if (typeof config === 'function') {
      this.#by = config
      this.#stateKey = 'seen'
      this.#max = 500
    } else if (typeof config === 'string') {
      this.#by = item => getPath(item, config)
      this.#stateKey = 'seen'
      this.#max = 500
    } else {
      this.#by = typeof config.by === 'function' ? config.by : (item: unknown) => getPath(item, config.by as string)
      this.#stateKey = config.stateKey ?? 'seen'
      this.#max = config.max ?? 500
    }
  }

  protected delta(items: unknown[], state: Record<string, unknown> | null): PollingResult {
    const currentIds = items.map(this.#by).map(id => `${ id }`)

    // Seed on first poll: remember ids, emit nothing.
    if (!state?.[this.#stateKey]) {
      return { events: [], state: { [this.#stateKey]: this.#buildSeen(currentIds) } }
    }

    const seen = state[this.#stateKey] as Record<string, number>
    const events = items.filter(item => !seen[`${ this.#by(item) }`])

    return { events, state: { [this.#stateKey]: this.#buildSeen([...Object.keys(seen), ...currentIds]) } }
  }

  // Bounded id → 1 map, keeping the most-recent `max` ids (dedup, last occurrence wins).
  #buildSeen(ids: string[]): Record<string, number> {
    return [...new Set(ids)]
      .slice(-this.#max)
      .reduce<Record<string, number>>((acc, id) => ({ ...acc, [id]: 1 }), {})
  }
}

// ── watermark (marker) ───────────────────────────────────────────────────────

// Markers are compared with JS relational ordering, which is exactly what lets one `by` field work whether
// the provider hands out ISO timestamps, sortable ids or plain numbers — the author never declares which.
const isNewer = (value: unknown, marker: unknown): boolean => (value as string) > (marker as string)

class WatermarkMechanism extends FetchingMechanism {
  readonly #stateKey: string

  constructor(fetch: FetchFn, private readonly config: WatermarkConfig) {
    super(fetch)

    this.#stateKey = config.stateKey ?? 'watermark'
  }

  protected delta(
    items: unknown[],
    state: Record<string, unknown> | null,
    params: Record<string, unknown>
  ): PollingResult {
    const valueOf = (item: unknown): unknown =>
      typeof this.config.by === 'function' ? this.config.by(item, { params }) : getPath(item, this.config.by)

    const advance = (start: unknown): unknown =>
      items.reduce((max, item) => {
        const value = valueOf(item)

        if (value == null) {
          return max
        }

        return max == null || isNewer(value, max) ? value : max
      }, start)

    // Seed on first poll: store the newest marker, emit nothing.
    if (state?.[this.#stateKey] === undefined || state?.[this.#stateKey] === null) {
      return { events: [], state: { [this.#stateKey]: advance(undefined) } }
    }

    const marker = state[this.#stateKey]
    const events: unknown[] = []

    for (const item of items) {
      const value = valueOf(item)

      if (value != null && isNewer(value, marker)) {
        events.push(item)
      } else if (this.config.sortedDesc) {
        // newest-first list → the first non-newer item means the rest are older too.
        break
      }
    }

    return { events, state: { [this.#stateKey]: advance(marker) } }
  }
}
